import { FC } from 'react';
import { masterSelector } from '@doda/common/src/redux/features/master';
import {
  AccordionList,
  TopAccordionListItem,
  MiddleAccordionListItem,
} from '@doda/io-react/common';
import {
  OCCUPATIONS_PARAM,
  PREFECTURE_PARAM,
  INDUSTRY_PARAM,
  EMPLOYMENT_STATUS_PARAM,
  HA_SEARCH_ITEMS_PARAM,
  OP_SEARCH_ITEMS_PARAM,
  OTHER_EMPLOYMENT_STATUS,
  OTHER_HA_SEARCH_ITEMS,
  OTHER_OP_SEARCH_ITEMS,
  JOB_TREND_URL,
  JOB_RANKING_URL,
  ABROAD_AREAS_PARAM,
} from '@doda/common/src/constants/seo/jobContentCommonConstants';
import {
  NATIONAL_AREA_NUMBER,
  ABROAD_AREA_NUMBER,
} from '@doda/common/src/constants/properties/const';
import { formatString } from '@doda/common/src/utils/stringUtil';
import { OTHER_JOB_TREND_TITLE } from '@doda/common/src/constants/seo/jobTrendConstants';
import { OTHER_JOB_RANKING_TITLE } from '@doda/common/src/constants/seo/jobRankingConstants';
import { useAppSelector } from 'redux/hooks';
import { OtherJobContentInfoByCategory } from './OtherJobContentInfoByCategory';
import styles from './index.module.scss';

/**
 * カテゴリ別の求人コンテンツ情報のProps
 * @param title タイトル名
 */
interface Props {
  title: string;
}

export const OtherJobContentInfo: FC<Props> = ({ title }) => {
  const master = useAppSelector(masterSelector);
  const areas = master.area ?? [];
  const exclusionAbroadAndNationwide = areas.filter(
    (ar) => ar.id !== NATIONAL_AREA_NUMBER && ar.id !== ABROAD_AREA_NUMBER
  );
  const abroadAreas = areas.find((ar) => ar.id === ABROAD_AREA_NUMBER);
  const occupations = master.occupation ?? [];
  const industry = master.industry ?? [];

  // 遷移先URL
  let url: string;
  switch (title) {
    // 求人トレンド
    case OTHER_JOB_TREND_TITLE:
      url = JOB_TREND_URL;
      break;
    // 人気求人ランキング
    case OTHER_JOB_RANKING_TITLE:
      url = JOB_RANKING_URL;
      break;
    default:
      url = '';
      return null;
  }

  return (
    <>
      <h2 className={styles.otherJobContentInfo__title}>{title}</h2>
      {occupations && (
        <OtherJobContentInfoByCategory title="職種">
          {occupations.map((oc) => (
            <MiddleAccordionListItem
              heading={oc.searchJobTypeName}
              key={oc.searchJobTypeName}
              href={`${url}${formatString(OCCUPATIONS_PARAM, [oc.searchJobTypeId])}`}
            />
          ))}
        </OtherJobContentInfoByCategory>
      )}
      {exclusionAbroadAndNationwide && (
        <OtherJobContentInfoByCategory title="勤務地">
          <AccordionList>
            {exclusionAbroadAndNationwide.map((ar) => (
              <TopAccordionListItem heading={ar.name} key={ar.id}>
                {ar.prefectureList?.map((pr) => (
                  <MiddleAccordionListItem
                    heading={pr.name}
                    key={pr.name}
                    href={`${url}${formatString(PREFECTURE_PARAM, [pr.id])}`}
                  />
                ))}
              </TopAccordionListItem>
            ))}
            {abroadAreas && (
              <TopAccordionListItem
                heading={abroadAreas.name}
                key={abroadAreas.id}
              >
                {abroadAreas.prefectureList?.map((pr) => (
                  <MiddleAccordionListItem
                    heading={pr.name}
                    key={pr.name}
                    href={`${url}${formatString(ABROAD_AREAS_PARAM, [pr.id])}`}
                  />
                ))}
              </TopAccordionListItem>
            )}
          </AccordionList>
        </OtherJobContentInfoByCategory>
      )}
      {industry && (
        <OtherJobContentInfoByCategory title="業種">
          {industry.map((ind) => (
            <MiddleAccordionListItem
              heading={ind.name}
              key={ind.id}
              href={`${url}${formatString(INDUSTRY_PARAM, [ind.id])}`}
            />
          ))}
        </OtherJobContentInfoByCategory>
      )}
      <OtherJobContentInfoByCategory title="雇用形態">
        {OTHER_EMPLOYMENT_STATUS.map((emp) => (
          <MiddleAccordionListItem
            heading={emp.name}
            key={emp.id}
            href={`${url}${formatString(EMPLOYMENT_STATUS_PARAM, [emp.id])}`}
          />
        ))}
      </OtherJobContentInfoByCategory>
      <OtherJobContentInfoByCategory title="年収">
        {OTHER_HA_SEARCH_ITEMS.map((ha) => (
          <MiddleAccordionListItem
            heading={ha.seoText}
            key={ha.id}
            href={`${url}${formatString(HA_SEARCH_ITEMS_PARAM, [ha.id])}`}
          />
        ))}
      </OtherJobContentInfoByCategory>
      <OtherJobContentInfoByCategory title="こだわり">
        {OTHER_OP_SEARCH_ITEMS.map((op) => (
          <MiddleAccordionListItem
            heading={op.name}
            key={op.id}
            href={`${url}${formatString(OP_SEARCH_ITEMS_PARAM, [op.id])}`}
          />
        ))}
      </OtherJobContentInfoByCategory>
    </>
  );
};
